import get from 'lodash/get';
import PropTypes from 'prop-types';

import { Box, Button } from '@mui/material';

import OpenVendorContainer from './OpenVendorContainer';
import RejectVisitorContainer from './RejectVisitorContainer';

import ModalHandlerComponent from '../Modal/ModalHandlerComponent';
import SortableTableComponent from '../Table/SortableTableComponent';
import GiveAccessComponent from './GiveAccessComponent';
import NoteComponent from './NoteComponent';

import { customSorting, formatDate } from '../../utils';
import { getFullName } from '../Person/utils';

import { TABLE_CONSTANTS, TABLE_ID } from '../Table/constants';
import { GIVE_ACCESS, OPEN_VENDOR, REJECT_VISITOR } from './constants';

import CellPersonWithImage from '../Table/CellPersonWithImage';
import { StyledCallCenterTableContainer } from './styledComponents';

const getValidUntil = (validUntil) => {
  if (validUntil) {
    if (Date.now() > Date.parse(validUntil)) {
      return 'Expired';
    }
    return formatDate(validUntil, false);
  }
  return 'Permanent';
};

const CallCenterVisitorComponent = ({
  visitors,
  allowOpenVendors,
  openVendorNote,
  modal,
  modalProps,
  onModalShow,
  onOpenAccessPointForVisitor,
  disableIfNoPhoto,
}) => {
  const columns = [
    {
      Header: 'Name',
      id: 'name',
      headerClassName: 'sort',
      minWidth: 200,
      accessor: ({ person }) => <CellPersonWithImage person={person} />,
      filter: true,
      sortMethod: (a, b) =>
        customSorting(
          get(a, 'props.person.firstName', null),
          get(b, 'props.person.firstName', null),
        ),
      dataKey: ({ person }) => getFullName(person),
    },
    {
      Header: 'Unit',
      id: 'unitName',
      accessor: ({ unitName }) => unitName,
      headerClassName: 'sort',
      dataKey: 'unitName',
      filter: true,
    },
    {
      Header: 'Valid Until',
      id: 'validUntil',
      accessor: ({ validUntil }) => getValidUntil(validUntil),
      headerClassName: 'sort',
      dataKey: ({ validUntil }) => getValidUntil(validUntil),
      filter: true,
    },
    {
      Header: 'Comment',
      id: 'comments',
      accessor: ({ comments }) => comments,
      dataKey: 'comments',
      filter: false,
      sortable: false,
    },
  ];

  const onRowClick = (rowInfo) => onModalShow(GIVE_ACCESS, rowInfo?.original);

  const rowStyles = {
    style: {
      cursor: 'pointer',
    },
  };

  return (
    <section>
      <NoteComponent note={openVendorNote} />
      <StyledCallCenterTableContainer>
        <SortableTableComponent
          tableId={TABLE_ID.ccVisitors}
          title="CC - Visitors"
          columns={columns}
          defaultSort={TABLE_CONSTANTS.name}
          entries={visitors}
          tableDataType={null}
          onRowClick={onRowClick}
          rowStyles={rowStyles}
        />
      </StyledCallCenterTableContainer>
      {allowOpenVendors && (
        <Button
          variant="contained"
          disableElevation
          fullWidth
          color="primary"
          onClick={() => onModalShow(OPEN_VENDOR)}
        >
          Unlisted Vendor
        </Button>
      )}
      <Box mt="2rem" />
      {visitors?.length > 0 && (
        <Button
          variant="contained"
          disableElevation
          fullWidth
          color="secondary"
          onClick={() => onModalShow(REJECT_VISITOR)}
        >
          Reject Visitor
        </Button>
      )}
      {modal?.type === REJECT_VISITOR && (
        <ModalHandlerComponent {...modalProps}>
          <RejectVisitorContainer {...modalProps} />
        </ModalHandlerComponent>
      )}
      {modal?.type === OPEN_VENDOR && (
        <ModalHandlerComponent {...modalProps}>
          <OpenVendorContainer
            {...modalProps}
            openVendorNote={openVendorNote}
          />
        </ModalHandlerComponent>
      )}
      {modal?.type === GIVE_ACCESS && (
        <ModalHandlerComponent {...modalProps}>
          <GiveAccessComponent
            buttonLabel="Give access and notify host"
            disabledButtonLabel="Must upload photo for access"
            onOpenAccessPoint={onOpenAccessPointForVisitor}
            disableIfNoPhoto={disableIfNoPhoto}
            {...modal?.data}
          />
        </ModalHandlerComponent>
      )}
    </section>
  );
};

CallCenterVisitorComponent.defaultProps = {
  openVendorNote: '',
};

CallCenterVisitorComponent.propTypes = {
  visitors: PropTypes.instanceOf(Array).isRequired,
  modal: PropTypes.instanceOf(Object).isRequired,
  onModalShow: PropTypes.func.isRequired,
  onOpenAccessPointForVisitor: PropTypes.func.isRequired,
  modalProps: PropTypes.instanceOf(Object).isRequired,
  allowOpenVendors: PropTypes.bool.isRequired,
  disableIfNoPhoto: PropTypes.bool.isRequired,
  openVendorNote: PropTypes.string,
};

export default CallCenterVisitorComponent;
